import {create} from 'javascript-state-machine';

import {Event, JSM_INITIAL_EVENT} from '../enums/Event';
import {createVideoStartFailedReason} from '../enums/VideoStartFailedReason';
import type {AnalyticsStateMachineOptions} from '../types/AnalyticsStateMachineOptions';
import {AnalyticsEventBase} from '../types/EventData';
import {logger} from '../utils/Logger';
import {isFunction, isNumber} from '../utils/Utils';

import {AnalyticsStateMachine} from './AnalyticsStateMachine';
import {
  customStateMachineErrorCallback,
  getAllStatesBut,
  logMissingCallbackWarning,
  logStateMachineTransition,
  whileIn,
} from './stateMachineUtils';

export enum State {
  /**
   * This state was created only to be able to "cut" the {@link State.PLAYING} and {@link State.PAUSE}
   * and send playing/pause sample with current custom data, while calling {@link AdapterAPI.setCustomData} function
   * and setting the new custom data values.
   *
   * @remarks
   *
   * Our implementation relays on a specific state name "CUSTOMDATACHANGE" because it should call the
   * default "onleavestate" state machine callback {@link stateMachineCallbacks.customdatachange}.
   */
  CUSTOMDATACHANGE = 'CUSTOMDATACHANGE',
  ERROR = 'ERROR',
  PAUSE = 'PAUSE',
  PLAYING = 'PLAYING',
  READY = 'READY',
  /**
   * We track and consider the rebuffering state only when it comes from the {@link State.PLAYING},
   * aka. when the video was playing and stopped because it started to rebuffer.
   */
  REBUFFERING = 'REBUFFERING',
  /**
   * We track and consider the "seeking" state only when it comes from the {@link State.PLAYING},
   * aka. when the video has already started to play and is already playing.
   */
  SEEKING = 'SEEKING',
  /**
   * We do not track the "seeking" while the playback is paused!
   *
   * We should not produce a "seeking" sample, but rather we should just extend the existing "pause" sample.
   *
   * Why do we recognize this state?
   *  - because during the seeking, {@link Event.TIMECHANGED} is fired and if we would just use {@link State.PAUSE},
   *    it would trigger a JSM transition to {@link State.PLAYING} and break the tracking of current pause.
   *  - because once seeking is finished, we need to transition the JSM back to {@link State.PAUSE}
   *
   *  @remarks
   *
   *  To be sure state machine works correctly, we need to be sure only calling {@link Event.SEEK} from
   *  {@link State.PAUSE} should transition JSM to {@link State.SEEKING_IN_PAUSE}!
   */
  SEEKING_IN_PAUSE = 'SEEKING_IN_PAUSE',
  SETUP = 'SETUP',
  SOURCE_CHANGING = 'SOURCE_CHANGING',
  STARTUP = 'STARTUP',
  /**
   * The browser window was closed (window 'unload' event), aka. sender has stopped the casting
   */
  UNLOADED = 'UNLOADED',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

export class CAFv3AnalyticsStateMachine extends AnalyticsStateMachine {
  override createStateMachine(opts: AnalyticsStateMachineOptions): StateMachine.StateMachine {
    return create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        ...whileIn(State.SETUP).passOver(
          // We track and consider the rebuffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
        ),

        {name: Event.SOURCE_LOADED, from: State.SETUP, to: State.READY},
        {name: Event.READY, from: State.SETUP, to: State.READY},

        ...whileIn(State.READY).passOver(
          Event.READY,
          // We track and consider the rebuffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
          // this will fire whenever the currentTime attribute changes
          Event.TIMECHANGED,
          Event.SEEK,
          Event.SEEKED,
        ),

        {name: Event.PLAY, from: State.READY, to: State.STARTUP},

        {name: Event.ERROR, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.UNLOAD, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.VIDEOSTART_TIMEOUT, from: State.STARTUP, to: State.VIDEOSTART_FAILED},

        {name: Event.ERROR, from: getAllStatesBut([State.STARTUP], State), to: State.ERROR},
        {name: Event.UNLOAD, from: getAllStatesBut([State.STARTUP], State), to: State.UNLOADED},

        // We need to wait for the 'TIME_UPDATE' event to also include startup video buffering in State.STARTUP time.
        //
        // We shouldn't use 'PLAYING' event, because that event is fired immediately, followed by buffering events that
        // would cut State.STARTUP sooner, then move us to State.PLAYING and then even to State.REBUFFERING.
        {name: Event.TIMECHANGED, from: State.STARTUP, to: State.PLAYING},
        ...whileIn(State.PLAYING).passOver(Event.TIMECHANGED),

        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.REBUFFERING},
        // While in State.REBUFFERING, we are waiting for 'TIME_UPDATE' event to be able to recognize end of the
        // rebuffering and to return back to State.PLAYING. The relay also on fact that State.REBUFFERING can only
        // start from State.PLAYING.
        //
        // We tried to use the 'BUFFERING' event containing `isBuffering: false`, but that was not reliable,
        // because of a scenario when the 'error' event would happen while rebuffering (because of rebuffering timeout
        // or Shaka player throwing error). Because this 'BUFFERING' event is also fired when player throws error, and
        // it would transition us to State.PLAYING and then it would send the short "playing" sample, before 'ERROR'
        // event would be fired and would move us to State.ERROR.
        {name: Event.TIMECHANGED, from: State.REBUFFERING, to: State.PLAYING},

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PAUSE, from: State.REBUFFERING, to: State.PAUSE},
        // if a 'PAUSE' event happens during State.SEEKING, we need to transition to State.PAUSE
        // instead of recovering back State.PLAYING (where State.SEEKING was started from)
        {name: Event.PAUSE, from: State.SEEKING, to: State.PAUSE},
        ...whileIn(State.PAUSE).passOver(
          // We track and consider the rebuffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
        ),
        // We use Event.PLAYING because it is more reliable for tracking transition from State.PAUSE to State.PLAYING.
        //
        // We shouldn't use 'TIME_UPDATE' to interrupt State.PAUSE, because that event is fired when the time indicated
        // by the currentTime property has been updated.
        //    Example: this happens when a user clicks 'PAUSE' during the seeking. Our state machine should move to
        //    State.PAUSE asap, but later because of the seeking, the events 'SEEKED' and 'TIME_UPDATE' are still fired,
        //    and that would break our state machine as we would end up in State.PLAYING
        {name: Event.PLAYING, from: State.PAUSE, to: State.PLAYING},

        {name: Event.SEEK, from: State.PAUSE, to: State.SEEKING_IN_PAUSE},
        ...whileIn(State.SEEKING_IN_PAUSE).passOver(
          Event.SEEK,
          // We track and consider the rebuffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
          // this will fire whenever the currentTime attribute changes
          Event.TIMECHANGED,
        ),
        {name: Event.SEEKED, from: State.SEEKING_IN_PAUSE, to: State.PAUSE},

        {name: Event.SEEK, from: State.PLAYING, to: State.SEEKING},
        ...whileIn(State.SEEKING).passOver(
          Event.SEEK,
          // We track and consider the rebuffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
          // this will fire whenever the currentTime attribute changes
          Event.TIMECHANGED,
        ),
        {name: Event.SEEKED, from: State.SEEKING, to: State.PLAYING},

        {
          name: Event.MANUAL_SOURCE_CHANGE,
          // We dont track source change during SETUP
          from: getAllStatesBut([State.SETUP], State),
          to: State.SOURCE_CHANGING,
        },
        ...whileIn(State.SOURCE_CHANGING).passOver(Event.START_BUFFERING, Event.TIMECHANGED),
        // Once the source is loaded, we are READY to receive play interaction
        {name: Event.SOURCE_LOADED, from: State.SOURCE_CHANGING, to: State.READY},

        // Because of this state transition, we are able to male "a cut" in the current PLAYING / PAUSE state
        // and send "playing" / "pause" sample with the current custom data values. The new changed custom data values
        // will be set on upcoming samples after we transition back from the CUSTOMDATACHANGE state.
        // For any other states, we don't care, because InternalAdapter is not emitting the event.
        {name: Event.CUSTOM_DATA_CHANGE, from: [State.PLAYING, State.PAUSE], to: State.CUSTOMDATACHANGE},
        {name: Event.PLAYING, from: State.CUSTOMDATACHANGE, to: State.PLAYING},
        {name: Event.PAUSE, from: State.CUSTOMDATACHANGE, to: State.PAUSE},
      ],

      callbacks: {
        [`onleave${State.PLAYING}`]: () => {
          this.playingHeartbeatService.stopHeartbeat();
        },

        [`onleave${State.STARTUP}`]: () => {
          this.clearVideoStartTimeout();
        },

        onleavestate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('LEAVE', event, from, to);

          // Skip this JSM callback for JSM initial event
          // This event is fired when the state machine is constructed,
          // to transition us from 'none' to custom initial 'SETUP' state
          if (event === JSM_INITIAL_EVENT) {
            return;
          }

          if (from === State.REBUFFERING) {
            this.resetRebufferingHelpers();
          }

          // Skip this JSM callback when an JSM event without a timestamp is called,
          // because it would cause NPE in analytics code (state duration calculation etc.).
          // This probably has happened because the state machine was not used correctly,
          // aka. JSM event was not called via `callEvent` method as it should be!
          if (!eventTimestamp) {
            return;
          }

          // Skip "default onleavestate analytics state machine callback" when leaving ERROR state,
          // because we call "error" state machine callback asap in "onafterEVENT" handler
          if (from === State.ERROR) {
            return;
          }

          // Skip, because we should ignore and do not track the "seeking" while the playback is paused
          // This means we should not use any callbacks, nor set any state properties
          if (
            (from === State.PAUSE && to === State.SEEKING_IN_PAUSE) ||
            (from === State.SEEKING_IN_PAUSE && to === State.PAUSE)
          ) {
            return;
          }

          // When leaving a state, before calling any callback (sending sample), we should set a correct sample `videoTimeEnd`
          if (eventData) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          }

          const stateDuration = eventTimestamp - this.onEnterStateTimestamp;
          const lowerCaseFrom = String(from).toLowerCase();

          // Calling a custom callback when leaving STARTUP into STARTUP_FAILURE
          if (from === State.STARTUP && to === State.VIDEOSTART_FAILED) {
            this.stateMachineCallbacks.videoStartFailed(stateDuration, createVideoStartFailedReason(event));
            return;
          }

          // Calling a custom callback when leaving SEEKING (started in the PAUSE/PLAY state)
          if (from === State.SEEKING) {
            this.stateMachineCallbacks.seeking(stateDuration);
            return;
          }

          // Calling a custom callback handling UNLOAD event
          if (event === Event.UNLOAD) {
            this.stateMachineCallbacks.unload(stateDuration, lowerCaseFrom);
            return;
          }

          // Default "onleavestate" state machine callback
          //
          // Every time, when we leave a state, we should try to call "state" callback,
          // to be able to make "a cut" and send analytics sample (if necessary)
          const defaultStateMachineCallbackFn = this.stateMachineCallbacks[lowerCaseFrom];
          if (isFunction(defaultStateMachineCallbackFn)) {
            // a try-catch block to add more resilience against callback errors
            try {
              defaultStateMachineCallbackFn(stateDuration, lowerCaseFrom, eventData);
            } catch (e) {
              logger.error('Exception occurred in State Machine callback ' + lowerCaseFrom, eventData, e);
            }
          } else {
            // if callback is not defined, log a warning to indicate to the developer that
            //  - a new callback function needs to be implemented to relay on this default state machine callback
            //  - or if no callback is needed, we should
            //    - early return from this JSM callback
            //    - or add this state to the ignored list
            logMissingCallbackWarning(from, [State.READY, State.SOURCE_CHANGING]);
          }
        },

        [`onenter${State.PLAYING}`]: () => {
          this.playingHeartbeatService.startHeartbeat();
        },

        [`onenter${State.SOURCE_CHANGING}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.manualSourceChange(eventData);
        },

        [`onenter${State.STARTUP}`]: () => {
          this.setVideoStartTimeout();
        },

        onenterstate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('ENTER', event, from, to);

          // Skip, because we should ignore and do not track the "seeking" while the playback is paused
          // This means we should not use any callbacks, nor set any state properties
          if (
            (from === State.PAUSE && to === State.SEEKING_IN_PAUSE) ||
            (from === State.SEEKING_IN_PAUSE && to === State.PAUSE)
          ) {
            return;
          }

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();
          // if customer set custom "start time" timestamp in AnalyticsStateMachineOptions of adapter constructor,
          // use it and override `onEnterStateTimestamp` during JSM initialization (transition to initial SETUP state)
          if (isNumber(opts.starttime) && event === JSM_INITIAL_EVENT) {
            this.onEnterStateTimestamp = opts.starttime;
          }

          if (eventData) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (to === State.REBUFFERING) {
            this.startRebufferingHeartbeatInterval();
          }
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.error(eventData);
        },
      },
    });
  }

  override onSsaiPlaybackInteraction(_timestamp: number, _eventObject: AnalyticsEventBase): void {
    // TODO: [AN-4774] Implement SSAI ad handling for CAFv3
    //  See: https://bitmovin.atlassian.net/browse/AN-4774
  }
}
